import { colors } from '../themes/colors';
import {
  type QueueCheckInfoModel,
  type QueueModel,
  type QueueStatus
} from '../types/models/queue-model';

const defaultCheckInfo: QueueCheckInfoModel = {
  ticketCount: 0,
  isQueueOpen: false,
  isQueueConnected: false,
  isCheckedIn: false,
  overdue: false,
  servedNow: false,
  queueTicketIndex: -1,
  myTicket: null
};

/**
 * Where the signed-in customer stands in a queue.
 *
 * Rewritten without rambda, whose `find` returns `T | undefined` while
 * `QueueCheckInfoModel.myTicket` is `TicketModel | null` — the mismatch was
 * invisible under the old non-strict tsconfig and surfaced at three call sites
 * as soon as it was type-checked. Declaring the return type here fixes it once
 * instead of casting at each caller.
 */
export function getQueueCheckInfo(
  queue: QueueModel | null | undefined
): QueueCheckInfoModel {
  if (!queue) {
    return defaultCheckInfo;
  }

  const base = {
    ticketCount: queue.tickets?.length || 0,
    isQueueOpen: queue.connectionStatus === 'open',
    isQueueConnected: queue.connectionStatus !== 'disconnected'
  };

  // Waiting in line.
  const queueTicketIndex = queue.tickets.findIndex((t) => t.isCheckedIn);
  if (queueTicketIndex !== -1) {
    return {
      ...base,
      isCheckedIn: true,
      overdue: false,
      servedNow: false,
      queueTicketIndex,
      myTicket: queue.tickets[queueTicketIndex] ?? null
    };
  }

  // Currently at a counter.
  const servedNowTicket = queue.allCurrentTickets.find((t) => t.isCheckedIn);
  if (servedNowTicket) {
    return {
      ...base,
      isCheckedIn: true,
      overdue: false,
      servedNow: true,
      queueTicketIndex: -1,
      myTicket: servedNowTicket
    };
  }

  // Called but missed. The server always sends an empty list now — the archive
  // this was computed from is gone — but the branch is kept so the screen
  // behaves the same if it is ever repopulated.
  const overdueTicket = queue.allOverdueTickets.find((t) => t.isCheckedIn);
  if (overdueTicket) {
    return {
      ...base,
      isCheckedIn: true,
      overdue: true,
      servedNow: false,
      queueTicketIndex: -1,
      myTicket: overdueTicket
    };
  }

  return { ...defaultCheckInfo, ...base };
}

export function getColorByQueueStatus(
  queueStatus: QueueStatus | null | undefined
) {
  switch (queueStatus) {
    case 'open': {
      return colors.primary;
    }
    case 'disconnected': {
      return colors.error;
    }
    default: {
      return colors.greyDarkest;
    }
  }
}
